import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { PaidPlanId } from '@financego/shared';

import { apiRequest, jsonBody } from '../../lib/api-client';
import type { PlanSummary, PublicPlan } from '../../lib/api-types';
import { userKeys } from '../users/users.api';

export const billingKeys = {
  all: ['billing'] as const,
  plans: ['billing', 'public-plans'] as const,
  summary: ['billing', 'plan-summary'] as const,
};

export async function fetchPublicPlans(): Promise<readonly PublicPlan[]> {
  return (await apiRequest<{ plans: PublicPlan[] }>('/api/plans/public')).plans;
}

export async function fetchPlanSummary(): Promise<PlanSummary> {
  return apiRequest<PlanSummary>('/api/plans/limits');
}

export async function checkAdviserAccess(): Promise<PlanSummary> {
  return (await apiRequest<{ access: PlanSummary }>('/api/plans/check-adviser')).access;
}

export async function createCheckoutSession(planId: PaidPlanId): Promise<string> {
  const result = await apiRequest<{ url: string }>('/api/stripe/create-checkout-session', {
    body: jsonBody({ planId }),
    headers: { 'idempotency-key': crypto.randomUUID() },
    method: 'POST',
  });
  if (!isTrustedCheckoutUrl(result.url)) throw new Error('Checkout URL is not trusted');
  return result.url;
}

export async function cancelSubscription(): Promise<{
  effectiveAt: string | null;
  status: 'scheduled';
}> {
  return (
    await apiRequest<{
      cancellation: { effectiveAt: string | null; status: 'scheduled' };
    }>('/api/stripe/cancel-subscription', { method: 'POST' })
  ).cancellation;
}

export function usePublicPlans() {
  return useQuery({ queryFn: fetchPublicPlans, queryKey: billingKeys.plans });
}

export function usePlanSummary() {
  return useQuery({ queryFn: fetchPlanSummary, queryKey: billingKeys.summary });
}

export function useAdviserAccess() {
  return useQuery({
    queryFn: checkAdviserAccess,
    queryKey: [...billingKeys.all, 'adviser-access'] as const,
    retry: false,
  });
}

export function useCheckout() {
  return useMutation({ mutationFn: createCheckoutSession });
}

export function useCancelSubscription() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: cancelSubscription,
    onSuccess: async () => {
      await Promise.all([
        client.invalidateQueries({ queryKey: billingKeys.all }),
        client.invalidateQueries({ queryKey: userKeys.current }),
      ]);
    },
  });
}

export function isTrustedCheckoutUrl(value: string): boolean {
  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'checkout.stripe.com';
  } catch {
    return false;
  }
}
